// One logline / plot atom of a scene, written from OUTSIDE the document — the
// scene's draft rows on the sheet, the Navigator, the plan. The document's own
// save of the active logline / plot line is `useUpdateScene`; both keep the
// scenes list the editor reads in step with a write that takes seconds
// (#1841, 22.09 item 4):
//
//   · the list takes the words as the save LEAVES — a row that remounts
//     meanwhile seeds from the list, and would seed the old text;
//   · a list read already on the wire is cancelled — it was asked before these
//     words existed, and landing on top of them is the vanish;
//   · an atom the save created joins the list under the server's id, never as
//     a stand-in — the next save names its atom from the list, and a stand-in's
//     id is one no save can address;
//   · until it has joined, the create is the field's: a save that names no atom
//     meanwhile — a second edit in the Navigator, the paper's own save of the
//     same line — updates what it is creating (`useSafeCreateOrUpdate`, keyed
//     by {@link sceneTextIdentity} on both sides), never creates a second atom.
//
// The atom is ONE line of the scene (ADR 0008 / #1835): a value with newlines
// is collapsed before it is written, listed or spliced into the paper.
//
// A refused write needs no rollback here: the settle invalidation re-reads the
// list either way, and the server still holds the old text.

import type { QueryKey } from '@tanstack/react-query';
import { request } from '@/api/apiUtils';
import { getActiveTextAtom } from '@/data/activeTextAtom';
import { fountainScenesEntity } from '@/data/entities/fountainScenes';
import { getPrefixes, useCustomMutation, useOptimisticPrefixPatch, useSafeCreateOrUpdate } from '@/data/mutations';
import { textAtomTypeId } from '@/enums';
import { useRequiredAppSelector } from '@/hooks';
import type { IFountainScene, TextAtoms } from '@/types';
import { toOneLine } from '@/utils/oneLine';
import { textAtomEndpoint, textAtomsEndpoint } from './useTextAtomMutations';

export interface SceneTextSaveParams {
    sceneId: number;
    content: string;
    /** The atom to write; absent = the field has no atom yet, so one is created. */
    textAtomId?: number;
    /** Optional callback to sync editor content when the text changes from the
     *  navigator — the document's line under the heading shows the same atom. */
    onEditorSync?: (sceneId: number, text: string) => void;
}

type AtomResponse = { data?: { id?: number }; id?: number };
const atomIdOf = (response: AtomResponse | undefined) => response?.data?.id ?? response?.id;

/** The create-or-update identity of a scene's logline / plot — shared with the
 *  document's scene save (`useUpdateScene`), so the two cannot both create it. */
export const sceneTextIdentity = (sceneId: number, field: 'logline' | 'plot') => `scene:${sceneId}:${field}`;

type SceneLists = Array<[QueryKey, IFountainScene[] | undefined]>;

/** The field's active atom as the list holds it NOW — what a save that names
 *  none writes to. Its caller read the id when the save was asked for, and a
 *  create from the other side may have joined the list since (the paper's save
 *  reaches its logline only after the body write answers). `undefined` = the
 *  field has no active atom: never had one, or every one is set aside. */
export const listedSceneTextAtomId = (lists: SceneLists, sceneId: number, field: 'logline' | 'plot') => {
    for (const [, scenes] of lists) {
        const scene = scenes?.find(s => s.id === sceneId);
        if (scene) return getActiveTextAtom('scene', field, scene.text_atoms)?.id;
    }
    return undefined;
};

export function useSceneTextSave({ action, typeId }: { action: 'updateLogline' | 'updateSynopsis'; typeId: textAtomTypeId }) {
    const projectId = useRequiredAppSelector('projectId');
    // `{ projectId }` without a version matches every versioned copy of the list.
    const list = useOptimisticPrefixPatch<IFountainScene[]>(fountainScenesEntity.fetchers.list.key({ projectId }));
    const patchScene = (sceneId: number, update: (scene: IFountainScene) => IFountainScene) =>
        list.patch(old => old?.map(scene => (scene.id === sceneId ? update(scene) : scene)));

    const field = typeId === textAtomTypeId.scene_logline ? 'logline' : 'plot';
    const upsert = useSafeCreateOrUpdate<{ sceneId: number; content: string; textAtomId?: number }, AtomResponse>({
        identityKey: ({ sceneId }) => sceneTextIdentity(sceneId, field),
        hasId: ({ sceneId, textAtomId }) => textAtomId ?? listedSceneTextAtomId(list.snapshot(), sceneId, field),
        create: async ({ sceneId, content }) => {
            const result = await request<AtomResponse>(textAtomsEndpoint({ project_id: projectId }), {
                method: 'POST',
                body: JSON.stringify({
                    textable_model: 'scene',
                    textable_id: sceneId,
                    text_atom_type_id: typeId,
                    content,
                }),
            });
            const id = atomIdOf(result);
            if (id === undefined) throw new Error('scene text atom create did not return id');
            return { id, data: result };
        },
        update: ({ content }, id) =>
            request<AtomResponse>(textAtomEndpoint({ project_id: projectId, id }), {
                method: 'PATCH',
                body: JSON.stringify({ content }),
            }),
    });

    return useCustomMutation<SceneTextSaveParams, AtomResponse>({
        mutationFn: ({ sceneId, content: raw, textAtomId }) => upsert({ sceneId, content: toOneLine(raw), textAtomId }),
        onMutate: async ({ sceneId, content: raw, textAtomId }) => {
            const content = toOneLine(raw);
            await list.cancelInFlight();
            // The atom the request writes, by IDENTITY — never every atom of the
            // type, which would rewrite a draft too (#1841-09).
            patchScene(sceneId, scene => ({
                ...scene,
                text_atoms: scene.text_atoms?.map(atom =>
                    textAtomId !== undefined && atom.id === textAtomId ? { ...atom, content } : atom,
                ),
            }));
        },
        onSuccess: (response, { sceneId, content: raw, textAtomId, onEditorSync }) => {
            const content = toOneLine(raw);
            const id = atomIdOf(response);
            if (id !== undefined) {
                patchScene(sceneId, scene => {
                    // Already listed: a save that named no atom updated one a
                    // create made meanwhile, so its onMutate had nothing to
                    // write — the words land now.
                    if (scene.text_atoms?.some(atom => atom.id === id)) {
                        if (textAtomId !== undefined) return scene;
                        return { ...scene, text_atoms: scene.text_atoms.map(atom => (atom.id === id ? { ...atom, content } : atom)) };
                    }
                    const created: TextAtoms = {
                        id,
                        text_atom_type_id: typeId,
                        content,
                        order: 0,
                        textable_id: sceneId,
                        textable_type: 'scene',
                        project_id: projectId,
                        tags: [],
                    };
                    return { ...scene, text_atoms: [...(scene.text_atoms ?? []), created] };
                });
            }
            onEditorSync?.(sceneId, content);
        },
        // Settled, the list names the atom (joined above, or re-read by the
        // invalidation below) — the next save reads its id from there.
        onSettled: (_data, _error, { sceneId, content }) => upsert.forget({ sceneId, content }),
        // [scenes, fountain.scenes] — prefix-covers every scene-keyed query
        invalidate: getPrefixes('scene', action),
    });
}
